/**
 * VideoPlayer — seamless looping video via dual-element swap.
 *
 * Uses two <video> elements. When one finishes, the other starts
 * playing from the beginning, creating a gapless loop transition.
 * The finished element is then reset and ready for the next swap.
 *
 * #36: Both elements now load the same src so the seamless swap works
 *      (previously the inactive element's src was stripped, causing a
 *      black screen after the first playthrough).
 * #25: Error/stall recovery added — onerror or a stalled load advances
 *      to the next item instead of freezing forever.
 * #180: Single-item slots can't "advance to the next item" on error (the
 *      advance is a no-op self-loop), which left an undecodable video as a
 *      permanent black screen. On error we now still ask the parent to advance
 *      ONCE (multi-item recovery, #25) AND schedule an in-place reload of the
 *      element with growing backoff. If the parent advanced (multi-item), the
 *      src changes and the pending retry is cancelled; if it didn't
 *      (single-item), the backoff reload retries the element quietly until a
 *      transient hiccup recovers — instead of black-screening dead.
 */

import { useRef, useEffect, useCallback, useState } from 'react';

export interface VideoPlayerProps {
  /** URL of the video to play (can be blob URL or cached URL) */
  src: string;
  /** Whether the video should loop (default: true) */
  loop?: boolean;
  /** Called when the video has played through once (for non-looping videos) */
  onEnded?: () => void;
  /** Called when the video starts playing */
  onPlay?: () => void;
  /** CSS class name for the video element */
  className?: string;
  /** When true, pauses the active video element (e.g. recycle overlay is visible). */
  paused?: boolean;
}

/** Stall timeout: if a video hasn't started playing in 10s, skip it (#25) */
const STALL_TIMEOUT_MS = 10_000;

/** #180: error-retry backoff bounds for reloading a broken/undecodable video */
const BASE_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

export function VideoPlayer({
  src,
  loop = true,
  onEnded,
  onPlay,
  className = '',
  paused = false,
}: VideoPlayerProps) {
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);
  const [activeVideo, setActiveVideo] = useState<'A' | 'B'>('A');
  // #180: bumping this forces the [src, reloadNonce] load effect to re-run and
  // reload + replay the element in place (the error-retry mechanism).
  const [reloadNonce, setReloadNonce] = useState(0);
  // #180: number of error-retries for the CURRENT src (drives the backoff);
  // reset on src change or on a successful (re)start.
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // #180: whether we've already asked the parent to advance for this src, so
  // repeated retry errors don't spam onEnded / proof-of-play.
  const advancedForSrcRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onPlayRef = useRef(onPlay);
  onPlayRef.current = onPlay;
  const loopRef = useRef(loop);
  loopRef.current = loop;

  const handleVideoEnded = useCallback((which: 'A' | 'B') => {
    if (!loopRef.current) {
      // Non-looping: notify parent
      onEndedRef.current?.();
      return;
    }

    // Seamless swap: activate the other video
    const nextVideo = which === 'A' ? 'B' : 'A';
    const nextRef = nextVideo === 'A' ? videoARef : videoBRef;

    // Start the next video immediately — it already has src loaded (#36)
    if (nextRef.current) {
      nextRef.current.currentTime = 0;
      nextRef.current.play().catch((err) => {
        console.warn('[VideoPlayer] Failed to play next video:', err);
      });
    }

    // Switch active video
    setActiveVideo(nextVideo);

    // Reset the just-finished video for next swap
    const finishedRef = which === 'A' ? videoARef : videoBRef;
    if (finishedRef.current) {
      finishedRef.current.currentTime = 0;
      finishedRef.current.pause();
    }
  }, []);

  // ─── Error/stall recovery (#25, #180) ────────────────────────────────
  // Schedule an in-place reload of the element after a growing backoff. Only
  // one retry is pending at a time; the src-change effect cancels it when the
  // parent advances to a different item (multi-item slots).
  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current) return;
    const delay = Math.min(BASE_RETRY_MS * 2 ** retryCountRef.current, MAX_RETRY_MS);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      retryCountRef.current += 1;
      console.warn(
        `[VideoPlayer] Retrying video after error (attempt ${retryCountRef.current})`,
      );
      setReloadNonce((n) => n + 1);
    }, delay);
  }, []);

  // If a video errors or stalls (corrupt file, unsupported codec, autoplay
  // rejected): ask the parent to advance to the next item ONCE (#25 — a
  // multi-item slot swaps in a different item, whose new src cancels the retry
  // below), and schedule an in-place backoff retry. On a single-item slot the
  // advance is a no-op self-loop, so the retry is what actually recovers the
  // element instead of leaving a dead black screen (#180). Subsequent retry
  // errors stay quiet — no repeat onEnded, no false proof-of-play every cycle.
  const handleError = useCallback(() => {
    console.error('[VideoPlayer] Video error');
    if (!advancedForSrcRef.current) {
      advancedForSrcRef.current = true;
      onEndedRef.current?.();
    }
    scheduleRetry();
  }, [scheduleRetry]);

  // Set up ended + error handlers on both elements
  useEffect(() => {
    const videoA = videoARef.current;
    const videoB = videoBRef.current;

    if (videoA) {
      videoA.onended = () => handleVideoEnded('A');
      videoA.onerror = handleError;
    }
    if (videoB) {
      videoB.onended = () => handleVideoEnded('B');
      videoB.onerror = handleError;
    }

    return () => {
      if (videoA) {
        videoA.onended = null;
        videoA.onerror = null;
      }
      if (videoB) {
        videoB.onended = null;
        videoB.onerror = null;
      }
    };
  }, [handleVideoEnded, handleError]);

  // ─── Reset error-retry bookkeeping on src change (#180) ───────────────
  // A new item starts fresh: a fresh backoff schedule, the ability to advance
  // again, and no stale pending retry from the previous (possibly broken) src.
  useEffect(() => {
    retryCountRef.current = 0;
    advancedForSrcRef.current = false;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [src]);

  // Clear any pending retry on unmount (#180).
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // ─── Load src into BOTH elements (#36) ────────────────────────────────
  // BOTH elements must carry the current src: the seamless loop swap
  // activates the other element, and playback becomes visible on A after
  // every src change. Loading only the "active" element left the other one
  // holding the PREVIOUS item's video — after the first loop swap, each new
  // src was loaded into the hidden element while the stale one (frozen on
  // the first playlist item) was made visible, so the screen replayed item
  // #1 between every real item while the real video played invisibly.
  useEffect(() => {
    const videoA = videoARef.current;
    const videoB = videoBRef.current;

    // Stall detection (#25): if the video hasn't started playing within
    // STALL_TIMEOUT_MS, skip it.
    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    if (videoB) {
      videoB.src = src;
      videoB.load();
      videoB.pause();
    }

    if (videoA) {
      videoA.src = src;
      videoA.load();
      videoA.play().then(() => {
        if (stallTimer) clearTimeout(stallTimer);
        // Recovered (or first successful start): reset the backoff so a future
        // hiccup starts from the base delay again (#180).
        retryCountRef.current = 0;
        onPlayRef.current?.();
      }).catch((err) => {
        if (stallTimer) clearTimeout(stallTimer);
        console.warn('[VideoPlayer] Autoplay failed:', err);
        // If autoplay is rejected, the onerror handler will fire on some
        // browsers; otherwise the stall timer handles it.
      });

      // Start stall timer (#25)
      stallTimer = setTimeout(() => {
        if (videoARef.current && videoARef.current.readyState < 2) {
          console.warn('[VideoPlayer] Video stalled — advancing');
          handleError();
        }
      }, STALL_TIMEOUT_MS);
    }

    // Playback restarts on element A for every new src (it now holds it)
    setActiveVideo('A');

    return () => {
      if (stallTimer) clearTimeout(stallTimer);
    };
    // reloadNonce (#180): a bump re-runs this effect to reload + replay the
    // element in place after an error backoff.
  }, [src, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suspend/resume playback (e.g. while the recycle overlay is visible) so
  // nothing plays or bills underneath the overlay.
  useEffect(() => {
    const activeRef = activeVideo === 'A' ? videoARef : videoBRef;
    if (!activeRef.current) return;
    if (paused) {
      activeRef.current.pause();
    } else {
      activeRef.current.play().catch(() => {
        /* autoplay rejection is handled elsewhere; nothing to do here */
      });
    }
  }, [paused, activeVideo]);

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      {/* disableRemotePlayback: without it Chromium paints its overlay Cast
          button (grey square, top-left) on each video for a few seconds at
          playback start — unwanted chrome on signage. */}
      <video
        ref={videoARef}
        muted
        playsInline
        preload="auto"
        disableRemotePlayback
        className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-0 ${
          activeVideo === 'A' ? 'opacity-100 z-10' : 'opacity-0 z-0'
        }`}
      />
      <video
        ref={videoBRef}
        muted
        playsInline
        preload="auto"
        disableRemotePlayback
        className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-0 ${
          activeVideo === 'B' ? 'opacity-100 z-10' : 'opacity-0 z-0'
        }`}
      />
    </div>
  );
}
